/**
 * `ScreenVisibilityContext` — boolean toggled by `<SdkScreen>` (RN) to
 * signal "the screen owning this subtree is in the foreground".
 *
 * **Why this exists.** On React Native with stack-based navigation
 * (expo-router), pushing a new screen onto the stack does NOT unmount
 * the previous screen — it just hides it. So every row's
 * `itemObserver(id)` ref stays attached and the SubjectRegistry keeps
 * the row's per-entity NATS subjects acquired, even though the user is
 * looking at another screen entirely.
 *
 * `<SdkScreen>` (the JSX wrapper) flips this context's boolean via
 * React Navigation's `useFocusEffect`. `<Row>` (from `useSdkRow`)
 * reads it and conditionally renders the `itemObserver` ref:
 *
 *   - focused → ref is `itemObserver(id)` → registry binds → subs
 *     acquired
 *   - blurred → ref is `null` → React detaches the previous ref
 *     (firing the registry's unbind handler) → subs released
 *
 * The React subtree stays mounted across navigation either way. No
 * remount, no unmount-flash, no FlashList re-virtualization. Only the
 * per-row NATS subject acquisitions toggle.
 *
 * Default `true` so consumers NOT using `<SdkScreen>` (web,
 * non-stack-based RN navigation, tests) get the normal "always
 * attached" behavior automatically.
 */

import { createContext } from 'react';

export interface ScreenVisibilityContextValue {
  /** True when the owning screen is focused. */
  readonly visible: boolean;
}

const DEFAULT: ScreenVisibilityContextValue = Object.freeze({ visible: true });

export const ScreenVisibilityContext = createContext<ScreenVisibilityContextValue>(DEFAULT);
